/**
 * Browser entry point.
 *
 * The QueryClientProvider is mounted by the router's root route, so the same
 * client serves guards, loaders and components (spec 14.7: TanStack Query owns
 * server truth).
 */
// FIRST, and it has to stay first. This file declares the cascade-layer order
// (theme, base, components, utilities) and the tokens; the component kit's own
// stylesheets are imported by the components, further down this import graph,
// and a layer that is first seen there would be ordered ahead of `base`.
import "./styles/app.css";
// Before anything that can reach a schema — only a stylesheet may come ahead of
// it. Zod must be told not to probe for `eval` before the first parse, or every
// page load raises a Content-Security-Policy violation. See the file.
import "./app/zod-csp.js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { createMadalineRouter } from "./app/router.js";
import { createQueryClient } from "./app/query-client.js";

const container = document.getElementById("root");
if (!container) throw new Error("index.html is missing #root");
const root = createRoot(container);

/**
 * `/dev/kit` — the component kit's kitchen sink, in DEVELOPMENT BUILDS ONLY.
 *
 * It is decided here, outside the router, on purpose: the page needs no
 * session, no query client and no route guard, and a route in the tree would
 * be one more thing a production build had to be proved not to contain.
 * `import.meta.env.DEV` is replaced by `false` at build time, so the branch,
 * the `import()` and the page's stylesheet are all removed from production
 * output (`test/kitchen-sink.test.tsx` reads this file to keep it that way).
 */
const KITCHEN_SINK_PATH = "/dev/kit";

if (import.meta.env.DEV && window.location.pathname.startsWith(KITCHEN_SINK_PATH)) {
  void import("./components/kit/dev/KitchenSink.js").then(({ KitchenSink }) => {
    document.title = "Component kit · Madaline";
    root.render(
      <StrictMode>
        <KitchenSink />
      </StrictMode>,
    );
  });
} else {
  const queryClient = createQueryClient();
  const router = createMadalineRouter({ queryClient });
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}
